/**
 * Mobile Debug Utility
 *
 * Category-based logging. Use this instead of console.log so logs can be
 * toggled by category and are consistent.
 *
 * Where logs go:
 * - Development: Metro bundler terminal (where you run `npx expo start`) and
 *   the device/simulator system log (Xcode console for iOS, logcat for Android).
 * - Production: Same console pipeline; in release builds you can gate category
 *   logs so only warn/error show unless categories are enabled via settings.
 *
 * Usage:
 *   import debug from '../utilities/debug';
 *
 *   debug.log('calendar', 'OAuth redirect received', { claim: '...' });
 *   debug.info('api', 'Request completed', { path, status });
 *   debug.warn('Unexpected state', data);
 *   debug.error('Request failed', err);
 */

import { store } from '../store';

let cachedSettings: { globalDebug: boolean; categories: Record<string, boolean> } | null = null;
let cacheTimestamp = 0;
const CACHE_TTL_MS = 30_000;

function getDebugSettings(): { globalDebug: boolean; categories: Record<string, boolean> } {
  const now = Date.now();
  if (cachedSettings && cacheTimestamp && now - cacheTimestamp < CACHE_TTL_MS) {
    return cachedSettings;
  }
  try {
    const state = store.getState();
    const settings = (state as any)?.session?.settings || {};
    const mobileCategories = settings.debug_categories?.mobile || settings.debug_categories?.frontend || settings.debug_categories || {};
    cachedSettings = {
      globalDebug: settings.debug === true,
      categories: typeof mobileCategories === 'object' && mobileCategories !== null ? mobileCategories : {},
    };
    cacheTimestamp = now;
    return cachedSettings;
  } catch {
    cachedSettings = { globalDebug: false, categories: {} };
    cacheTimestamp = now;
    return cachedSettings;
  }
}

function isCategoryEnabled(category: string): boolean {
  const settings = getDebugSettings();
  if (settings.globalDebug) return true;
  return settings.categories[category] === true;
}

/**
 * In __DEV__, if no categories are configured we show all category logs so
 * developers see output without configuring settings.
 */
function shouldLogCategory(category: string): boolean {
  if (typeof __DEV__ !== 'undefined' && __DEV__) {
    const settings = getDebugSettings();
    const hasAnyEnabled = settings.globalDebug || Object.values(settings.categories).some(Boolean);
    if (!hasAnyEnabled) return true;
  }
  return isCategoryEnabled(category);
}

function log(category: string, message: string, data?: unknown): void {
  if (!shouldLogCategory(category)) return;
  const prefix = `[DEBUG:${category}]`;
  if (data !== undefined) {
    console.log(prefix, message, data);
  } else {
    console.log(prefix, message);
  }
}

function info(category: string, message: string, data?: unknown): void {
  if (!shouldLogCategory(category)) return;
  const prefix = `[INFO:${category}]`;
  if (data !== undefined) {
    console.info(prefix, message, data);
  } else {
    console.info(prefix, message);
  }
}

function warn(message: string, data?: unknown): void {
  if (data !== undefined) {
    console.warn('[WARN]', message, data);
  } else {
    console.warn('[WARN]', message);
  }
}

function error(message: string, data?: unknown): void {
  if (data instanceof Error) {
    console.error('[ERROR]', message, { message: data.message, stack: data.stack, name: data.name });
  } else if (data !== undefined) {
    console.error('[ERROR]', message, data);
  } else {
    console.error('[ERROR]', message);
  }
}

function clearCache(): void {
  cachedSettings = null;
  cacheTimestamp = 0;
}

function getCategories(): string[] {
  return Object.keys(getDebugSettings().categories);
}

function getStatus(): { globalDebug: boolean; enabledCategories: string[]; allCategories: string[] } {
  const settings = getDebugSettings();
  const enabledCategories = Object.entries(settings.categories)
    .filter(([, enabled]) => enabled === true)
    .map(([cat]) => cat);
  return {
    globalDebug: settings.globalDebug,
    enabledCategories,
    allCategories: Object.keys(settings.categories),
  };
}

const debug = {
  log,
  info,
  warn,
  error,
  isEnabled: isCategoryEnabled,
  clearCache,
  getCategories,
  getStatus,
};

export default debug;
